import React from "react";
import Link from "next/link";
import Image from "next/image";
import { FOOTER_CONFIG } from "./constants";

const Logo = () => (
  <Image
    src={FOOTER_CONFIG.logo.src}
    alt={FOOTER_CONFIG.logo.alt}
    width={160}
    height={60}
    className="h-16 w-auto"
    priority
  />
);

function FooterLink({ label, href }: { label: string; href: string }) {
  return (
    <a
      href={href}
      className="text-gray-400 hover:text-white transition-colors text-sm"
    >
      {label}
    </a>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: Array<{ id: string; label: string; href: string }>;
}) {
  return (
    <div>
      <h3 className="text-white font-semibold text-sm mb-4">{title}</h3>
      <div className="space-y-3 flex flex-col gap-2">
        {links.map((link) => (
          <FooterLink key={link.id} label={link.label} href={link.href} />
        ))}
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-dark text-gray-400 border-t border-gray-900">
      <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24">
        {/* Top section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Logo and tagline */}
          <div className="lg:col-span-2">
            <Link href={FOOTER_CONFIG.logo.href}>
              <Logo />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mt-4 max-w-sm">
              {FOOTER_CONFIG.tagline}
            </p>
          </div>

          {/* Navigation columns */}
          {FOOTER_CONFIG.columns.map((column) => (
            <FooterColumn
              key={column.id}
              title={column.title}
              links={column.links}
            />
          ))}

          {/* Contact column */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-4">
              {FOOTER_CONFIG.contact.label}
            </h3>
            <a
              href={FOOTER_CONFIG.contact.href}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {FOOTER_CONFIG.contact.email}
            </a>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-500 py-8" />

        {/* Bottom section */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <p className="text-gray-400 text-xs">{FOOTER_CONFIG.copyright}</p>
          <p className="text-gray-400 text-xs leading-relaxed max-w-lg">
            {FOOTER_CONFIG.disclaimer}
          </p>
        </div>
      </div>
    </footer>
  );
}
